import type { TOC } from '@ember/component/template-only';
import { PrismBanner, PrismButton } from '@mylighthouse/prism-ember';
import { ICONS_LOGOS_MAP } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { FocusScreen, StepIndicator } from 'frontend-utils';
import HotelWizardsPreferredRateOnboardingPreferredRateOnboardingOptions from 'lumen/components/hotel/wizards/preferred-rate-onboarding/preferred-rate-onboarding-options';
import type HotelWizardsPreferredRateOnboardingRoomTypeController from 'lumen/controllers/hotel/wizards/preferred-rate-onboarding/room-type';
import { PREFERRED_RATE_ONBOARDING_TOTAL_STEPS } from 'lumen/utils/constants/preferred-rate-onboarding';

interface HotelWizardsPreferredRateOnboardingRoomTypeSignature {
  Args: {
    controller: HotelWizardsPreferredRateOnboardingRoomTypeController;
    model: unknown;
  };
}

<template>
  <FocusScreen
    @hasCancel={{false}}
    @isCancelDisabled={{true}}
    @logoHtml={{ICONS_LOGOS_MAP.logo-dark-with-text}}
  >
    <:header>
      <div class="wizards-preferred-rate-onboarding__header">
        <h3>{{t "wizards.preferred_rate_onboarding.header"}}</h3>
        <div class="wizards-preferred-rate-onboarding__header__step-indicator">
          <StepIndicator
            @currentStep={{2}}
            @steps={{PREFERRED_RATE_ONBOARDING_TOTAL_STEPS}}
          />
        </div>
      </div>
    </:header>

    <:body>
      <div class="wizards-preferred-rate-onboarding__body">
        <div class="wizards-preferred-rate-onboarding__content">
          <h1 class="mb-400">
            {{t "wizards.preferred_rate_onboarding.room_type.content.title"}}
          </h1>

          <h4 class="text-300--regular mb-600">
            {{t
              "wizards.preferred_rate_onboarding.room_type.content.sub_title"
            }}
          </h4>

          {{#unless
            @controller.model.requestPricingAssistantRatePreferencesOptionsTaskResult.isRunning
          }}
            <HotelWizardsPreferredRateOnboardingPreferredRateOnboardingOptions
              @options={{@controller.roomTypeOptions}}
              @updateOption={{@controller.updateSelectedRoomType}}
            />
          {{/unless}}

          <div class="wizards-preferred-rate-onboarding__content--banner">
            <PrismBanner
              @bannerMessage={{t
                "wizards.preferred_rate_onboarding.room_type.banner.message"
              }}
              @bannerStyle="info"
              @bannerTitle={{t
                "wizards.preferred_rate_onboarding.room_type.banner.title"
              }}
              @icon="circle-information-outline"
            />
          </div>
        </div>
      </div>
    </:body>

    <:footer>
      <div
        class="wizards-preferred-rate-onboarding__footer wizards-preferred-rate-onboarding__footer--with-back-button"
      >
        <PrismButton
          @buttonType="ghost"
          @iconBefore="arrow-left"
          @label={{t "general.back"}}
          @onClick={{@controller.onBack}}
          @size="large"
        />

        <div class="flex gap-300">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.user_details_skip_button"}}
            @onClick={{@controller.onSkip}}
            @size="large"
          />

          <PrismButton
            @buttonType="brand"
            @iconAfter="arrow-right"
            @label={{t "general.continue"}}
            @onClick={{@controller.onContinue}}
            @size="large"
          />
        </div>
      </div>
    </:footer>
  </FocusScreen>
</template> satisfies TOC<HotelWizardsPreferredRateOnboardingRoomTypeSignature>;
